/**
 * 【法币展示/决策 统一逻辑】法币区域 / Fiat-M 与首页资产展示
 * 统一只信 recommendedCurrencyList：
 * - plates 中包含 258 的币种视为法币
 * - 不再做前端本地 IP / 余额 / fiatCurrencyIds 兜底筛选
 */
import { Storage, Util, Request } from "@xt/xtbase";

const { getOrigin } = Util;
const { XtAxios4 } = Request;

const IP_API_URL = getOrigin("www") + "/sapi/v4/ip/public/info/ip";
const RECOMMENDED_FIAT_PLATE_ID = 258;

/**
 * 从 fund.recommendedCurrencyList（或同结构数组）解析推荐法币代码，与 Market 页 plate 258 规则一致。
 * @param {Array<{ currency?: string, coinName?: string, plates?: number[] }>} recommendedCurrencyList
 * @returns {string[]}
 */
export function getRecommendedFiatCodesFromList(recommendedCurrencyList = []) {
  if (!Array.isArray(recommendedCurrencyList)) return [];
  return recommendedCurrencyList.reduce((result, item) => {
    const code = String(item?.currency || item?.coinName || "").toUpperCase();
    const plates = Array.isArray(item?.plates) ? item.plates : [];
    const isFiat = plates.some((plateId) => Number(plateId) === RECOMMENDED_FIAT_PLATE_ID);
    if (isFiat && code && !result.includes(code)) {
      result.push(code);
    }
    return result;
  }, []);
}

/** 测试用：设为 "AE" 等时 IP 接口被跳过，直接返回该国家代码；设为 null 使用真实 IP */
const IP_MOCK_COUNTRY = null;

function getRecommendedCurrencies() {
  const payload = Storage.get("recommendedCurrencyList") || {};
  return Array.isArray(payload.currencies) ? payload.currencies : [];
}

export function getDynamicFiatMarketCodes() {
  const currencies = getRecommendedCurrencies();
  const codes = [];
  currencies.forEach((item) => {
    const code = String(item?.currency || item?.currencyCode || item?.coinName || "").toUpperCase();
    const plates = Array.isArray(item?.plates) ? item.plates : [];
    const isFiat = plates.some((plateId) => Number(plateId) === RECOMMENDED_FIAT_PLATE_ID);
    if (isFiat && code && !codes.includes(code)) {
      codes.push(code);
    }
  });
  return codes;
}

export function isFiatMarketCode(code) {
  if (!code) return false;
  return getDynamicFiatMarketCodes().includes(String(code).toUpperCase());
}

function getCurrencyMapFromRecommendedCurrencies() {
  const currencies = getRecommendedCurrencies();
  const map = {};
  currencies.forEach((item) => {
    const id = item.id ?? item.currencyId ?? item.coinId;
    const code = item.currency || item.currencyCode;
    if (id != null && code) {
      map[id] = (code + "").toUpperCase();
    }
  });
  return map;
}

/**
 * 从 recommendedCurrencyList 构建 coinId -> 币种代码 的映射
 * @returns {Record<string, string>}
 */
export function getFiatCurrencyMapFromStorage() {
  return getCurrencyMapFromRecommendedCurrencies();
}

/**
 * 通过 IP 获取国家代码
 * @returns {Promise<string|null>} 如 "AE", "BR", "SA"
 */
export async function getCountryCodeByIP() {
  try {
    if (IP_MOCK_COUNTRY) return IP_MOCK_COUNTRY;

    const response = await XtAxios4.get(IP_API_URL);
    const countryCode = response?.countryCode || response?.data?.countryCode;
    return countryCode ? countryCode.toUpperCase() : null;
  } catch (error) {
    console.warn("fiatMarket: getCountryCodeByIP failed", error);
    return null;
  }
}

/**
 * 兼容旧返回结构：法币列表直接来自 recommendedCurrencyList
 * @param {{ fiatCurrencyIds?: number[] }} options
 * @returns {Promise<{ fromFiatIds: string[], fromIP: string | null }>}
 */
export async function resolveFiatCodesForList(options = {}) {
  const allowedCodes = getDynamicFiatMarketCodes();
  return { fromFiatIds: allowedCodes, fromIP: null };
}

/**
 * 【法币提现专用】返回允许展示的法币列表及默认选中
 * @param {{ fiatCurrencyIds?: number[], fiatCodesWithBalance?: string[] }} options
 * @returns {Promise<{ allowedCodes: string[], primaryFiat: string }>}
 */
export async function resolveAllowedFiatCodesForWithdraw(options = {}) {
  const dynamicCodes = getDynamicFiatMarketCodes();
  return { allowedCodes: dynamicCodes, primaryFiat: dynamicCodes[0] || "USDT" };
}

/**
 * 返回允许展示的法币代码列表，用于 Convert/充值/提现等
 * @param {{ fiatCurrencyIds?: number[], fiatCodesWithBalance?: string[] }} options
 * @returns {Promise<{ allowedCodes: string[], fromFiatIds: string[], fromIP: string | null }>}
 */
export async function resolveAllowedFiatCodesForDisplay(options = {}) {
  const dynamicCodes = getDynamicFiatMarketCodes();
  return { allowedCodes: dynamicCodes, fromFiatIds: dynamicCodes, fromIP: null };
}

/**
 * 判断是否应展示法币相关 UI（如 Fiat-M tab），并返回允许展示的法币代码列表
 * @param {{ fiatCurrencyIds?: number[], fiatCodesWithBalance?: string[] }} options fiatCodesWithBalance 为有法币余额的代码列表，需调用方拉取后传入
 * @returns {Promise<{ showFiatTab: boolean, fiatCode?: string, fiatCodes: string[] }>}
 */
export async function resolveFiatMarketEligible(options = {}) {
  const dynamicCodes = getDynamicFiatMarketCodes();
  const showFiatTab = dynamicCodes.length > 0;
  return {
    showFiatTab,
    fiatCode: dynamicCodes[0] || undefined,
    fiatCodes: dynamicCodes,
  };
}

/**
 * 解析余额/汇率等场景下的法币选项
 * @param {{ fiatCurrencyIds?: number[], fiatCodesWithBalance?: string[] }} options
 * @returns {Promise<{ options: string[], primaryFiat: string, selectable: boolean }>}
 */
export async function resolveFiatOptionsForBalance(options = {}) {
  const dynamicCodes = getDynamicFiatMarketCodes();
  const options_ = dynamicCodes.length > 0 ? ["USDT", ...dynamicCodes] : ["USDT"];
  const primaryFiat = dynamicCodes[0] || "USDT";
  const selectable = options_.length > 1;
  return { options: options_, primaryFiat, selectable };
}
